if (!customElements.get("custom-design-steps")) {
  customElements.define(
    "custom-design-steps",
    class CustomDesignSteps extends HTMLElement {
      connectedCallback() {
        this.flow = this.querySelector("[data-steps-flow]");
        this.svg = this.querySelector("[data-steps-connectors]");
        this.pathGroup = this.querySelector("[data-steps-paths]");
        this.markerId = this.svg?.dataset.markerId;
        this.frame = null;

        if (!this.flow || !this.svg || !this.pathGroup) return;

        this.handleResize = this.scheduleDraw.bind(this);
        window.addEventListener("resize", this.handleResize, { passive: true });

        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.flow);
        this.getItems().forEach((item) => this.resizeObserver.observe(item));

        this.mutationObserver = new MutationObserver(() => {
          this.resizeObserver.disconnect();
          this.resizeObserver.observe(this.flow);
          this.getItems().forEach((item) => this.resizeObserver.observe(item));
          this.scheduleDraw();
        });
        this.mutationObserver.observe(this.flow, { childList: true });

        this.querySelectorAll("img").forEach((image) => {
          if (!image.complete) {
            image.addEventListener("load", this.handleResize, { once: true });
          }
        });

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.scheduleDraw());
        }

        this.scheduleDraw();
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.handleResize);
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      getItems() {
        return Array.from(this.querySelectorAll("[data-step-item]"));
      }

      scheduleDraw() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          this.drawConnectors();
          this.frame = null;
        });
      }

      drawConnectors() {
        const items = this.getItems();
        this.pathGroup.replaceChildren();

        if (items.length < 2) return;

        const flowRect = this.flow.getBoundingClientRect();
        if (!flowRect.width || !flowRect.height) return;

        this.svg.setAttribute("viewBox", `0 0 ${flowRect.width} ${flowRect.height}`);
        const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

        items.slice(0, -1).forEach((item, index) => {
          const nextItem = items[index + 1];
          const startMedia = item.querySelector("[data-step-media]") || item;
          const endMedia =
            nextItem.querySelector("[data-step-media]") || nextItem;
          const startText =
            item.querySelector(".custom-design-steps__text") || item;
          const pathData = this.createPath(
            this.getLayoutBounds(startMedia, flowRect),
            this.getLayoutBounds(endMedia, flowRect),
            this.getLayoutBounds(item, flowRect),
            this.getLayoutBounds(nextItem, flowRect),
            this.getLayoutBounds(startText, flowRect),
            index,
            isDesktop
          );

          if (!pathData) return;

          const path = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "path"
          );

          path.setAttribute("class", "custom-design-steps__connector");
          path.setAttribute("d", pathData);
          path.setAttribute("marker-end", `url(#${this.markerId})`);
          this.pathGroup.appendChild(path);
        });
      }

      createPath(
        startMediaRect,
        endMediaRect,
        startItemRect,
        endItemRect,
        startTextRect,
        index,
        isDesktop
      ) {
        const mediaVerticalOverlap =
          Math.min(startMediaRect.bottom, endMediaRect.bottom) -
          Math.max(startMediaRect.top, endMediaRect.top);
        const useHorizontal =
          isDesktop &&
          mediaVerticalOverlap >
            Math.min(startMediaRect.height, endMediaRect.height) * 0.35;
        const startRect = useHorizontal ? startMediaRect : startItemRect;
        const endRect = isDesktop ? endMediaRect : endItemRect;
        const bias = index % 2 === 0 ? 0.36 : 0.64;
        const [closestStartX, endX] = this.getClosestAxisPoints(
          startRect.left,
          startRect.right,
          endRect.left,
          endRect.right,
          bias
        );
        const horizontalDirection = Math.sign(endX - closestStartX) || 1;
        let startX = closestStartX;

        if (isDesktop && index === 1) {
          startX += horizontalDirection * 24;
        } else if (isDesktop && index === 2) {
          startX -=
            horizontalDirection * Math.min(72, startMediaRect.width * 0.28);
        }

        const [closestStartY, endY] = this.getClosestAxisPoints(
          startRect.top,
          startRect.bottom,
          endRect.top,
          endRect.bottom,
          bias
        );
        const startY =
          isDesktop && index === 1
            ? startTextRect.bottom - Math.min(20, startTextRect.height * 0.25)
            : closestStartY;
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const distance = Math.hypot(deltaX, deltaY);

        if (distance < 1) return "";

        const unitX = deltaX / distance;
        const unitY = deltaY / distance;
        const handle = distance * 0.34;
        const bendMagnitude = Math.min(
          52,
          Math.max(22, distance * 0.14),
          distance * 0.3
        );
        const normalX = -unitY;
        const normalY = unitX;
        let bendDirection = index % 2 ? -1 : 1;

        if (isDesktop) {
          bendDirection = normalY > 0 ? -1 : 1;
        }

        const bend = bendMagnitude * bendDirection;
        const controlOne = {
          x: startX + unitX * handle + normalX * bend,
          y: startY + unitY * handle + normalY * bend,
        };
        const controlTwo = {
          x: endX - unitX * handle + normalX * bend,
          y: endY - unitY * handle + normalY * bend,
        };

        return `M ${startX} ${startY} C ${controlOne.x} ${controlOne.y}, ${controlTwo.x} ${controlTwo.y}, ${endX} ${endY}`;
      }

      getLayoutBounds(element, flowRect) {
        let left = 0;
        let top = 0;
        let current = element;

        while (current && current !== this.flow) {
          left += current.offsetLeft;
          top += current.offsetTop;
          current = current.offsetParent;
        }

        if (current !== this.flow) {
          const rect = element.getBoundingClientRect();

          left = rect.left - flowRect.left;
          top = rect.top - flowRect.top;
        }

        return {
          left,
          right: left + element.offsetWidth,
          top,
          bottom: top + element.offsetHeight,
          width: element.offsetWidth,
          height: element.offsetHeight,
        };
      }

      getClosestAxisPoints(startMin, startMax, endMin, endMax, bias) {
        if (startMax <= endMin) return [startMax, endMin];
        if (endMax <= startMin) return [startMin, endMax];

        const overlapStart = Math.max(startMin, endMin);
        const overlapEnd = Math.min(startMax, endMax);
        const sharedPoint = overlapStart + (overlapEnd - overlapStart) * bias;

        return [sharedPoint, sharedPoint];
      }
    }
  );
}
